import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api/axios";
import {
  STATUS_CHIP, CATEGORY_CHIP, ISSUE_CHIP, Chip, issueOf, summarizeIssues,
} from "./SupportList";

const BASE_URL = process.env.REACT_APP_API_URL;

/* ─── Brand palette ──────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D", stone: "#4C5C46",
};

const LABEL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-muted)", marginBottom: 8, textTransform: "uppercase",
};

const INPUT = {
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: "1.5px solid var(--mly-border)", fontSize: 14, boxSizing: "border-box", outline: "none",
};

const TH = {
  textAlign: "left", padding: "8px 10px", fontSize: 11,
  color: "var(--mly-text-muted)", textTransform: "uppercase", fontWeight: 700,
};

const POLL_MS = 15000;

const imgSrc = (p) => {
  if (!p) return "";
  const s = String(p);
  if (/^https?:\/\//i.test(s)) return s;
  const clean = s.replace(/^\/+/, "");
  if (clean.startsWith("uploads/")) return `${BASE_URL}/${clean}`;
  return `${BASE_URL}/uploads/${clean}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

/* every amount on the service is an integer number of PAISE */
const rupeesOf = (paise) => (Number(paise) || 0) / 100;

/* first value that reads as a non-negative integer, else null */
const firstInt = (...vals) => {
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i];
    if (v === undefined || v === null || v === "") continue;
    const n = Number(v);
    if (Number.isInteger(n) && n >= 0) return n;
  }
  return null;
};

const posQty = (v, fallback = 1) => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/* ─── Normalisers ─────────────────────────────────────────────────────────
   GET /admin/support/tickets/{id} answers {data: <ticket>} — one of the few
   routes whose own payload has a top-level `data` key — and the ticket is
   camelCase throughout: customerName/customerPhone are flat, orderNo is what
   addresses the order, and resolution money is integer PAISE. ── */

const normalizeItem = (raw) => ({
  /* a ticket line points at an ORDER LINE id now, never an array index */
  orderItemId: firstInt(raw?.orderItemId),
  name:    raw?.name || "",
  variant: raw?.variantLabel || "",
  quantity: posQty(raw?.quantity),
  issue:   issueOf(raw),
});

const normalizeTicket = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id === undefined || raw.id === null ? "" : String(raw.id);
  if (!id) return null;

  const rz = raw.resolution || {};

  return {
    id,
    ticketNo:    raw.ticketNo || "",
    subject:     raw.subject || "",
    description: raw.description || "",
    category:    raw.category || "other",
    status:      raw.status || "open",
    images:      Array.isArray(raw.images) ? raw.images : [],
    messages:    Array.isArray(raw.messages) ? raw.messages : [],
    items:       (Array.isArray(raw.items) ? raw.items : []).map(normalizeItem),
    resolution: {
      type:   rz.type || "",
      /* amountPaise, not amount — paise in, rupees out */
      amount: rupeesOf(rz.amountPaise),
      refId:  rz.refId || "",
      notes:  rz.notes || "",
    },
    createdAt:   raw.createdAt || null,
    updatedAt:   raw.updatedAt || null,
    /* the internal row id is not a URL — orderNo is what addresses an order */
    orderNo:     raw.orderNo || "",
    customer: {
      name:  raw.customerName || "—",
      phone: raw.customerPhone || "",
    },
  };
};

/* a dispatch answers {data:{ticket, replacementOrder:{orderId, orderNo, …}}};
   orderNo is both the toast text and the deep link */
const newOrderNoOf = (body) => body?.data?.replacementOrder?.orderNo || "";

const DISPATCH = {
  replacement: {
    path:    "dispatch_replacement",
    title:   "Dispatch replacement",
    blurb:   "Send these items again at zero charge. The ticket stays open for the conversation.",
    success: "Replacement dispatched",
    cta:     "Dispatch replacement",
  },
  missing: {
    path:    "send_missing",
    title:   "Send missing items",
    blurb:   "Ship what never arrived — a zero-charge order, and the customer is told it's on the way.",
    success: "Missing items dispatched",
    cta:     "Send missing items",
  },
};

const SupportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [ticket,  setTicket]  = useState(null);
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const [reply,     setReply]     = useState("");
  const [sending,   setSending]   = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const [lightbox, setLightbox] = useState(null); // image url or null

  /* quick dispatch panel (thread area) */
  const [dispatchMode,  setDispatchMode]  = useState(""); // "" | "replacement" | "missing"
  const [dispatchSel,   setDispatchSel]   = useState({}); // { [orderItemId]: qty }
  const [dispatchNotes, setDispatchNotes] = useState("");
  const [dispatchBusy,  setDispatchBusy]  = useState(false);

  /* resolve card */
  const [resolveMode, setResolveMode] = useState(""); // "" | "replacement" | "refund"
  const [replQty,     setReplQty]     = useState([]); // aligned to order.items
  const [replNotes,   setReplNotes]   = useState("");
  const [replBusy,    setReplBusy]    = useState(false);
  const [refundMode,  setRefundMode]  = useState("source"); // "source" | "yopoints"
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNotes,  setRefundNotes]  = useState("");
  const [refundBusy,   setRefundBusy]   = useState(false);

  const fetchedOrderFor = useRef(null);
  const threadEndRef = useRef(null);
  /* SupportList deep-links with { state: { action } } to open a panel on arrival */
  const pendingAction = useRef(location.state && location.state.action);

  const fetchTicket = useCallback(async (silent = false) => {
    try {
      const res = await api.get(`/admin/support/tickets/${id}`);
      /* {data: <ticket>} — the payload's OWN top-level key is `data` here */
      const t = normalizeTicket(res.data?.data);
      if (t) {
        setTicket(t);
        setError("");
      } else if (!silent) {
        setError("Ticket not found");
      }
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || "Failed to load ticket");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    fetchTicket();
    const t = setInterval(() => fetchTicket(true), POLL_MS);
    return () => clearInterval(t);
  }, [fetchTicket]);

  /* open the panel the list asked for, once */
  useEffect(() => {
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action === "replacement" || action === "missing") {
      setDispatchSel({});
      setDispatchMode(action);
    }
  }, []);

  /* load the linked order (for the replacement table + refund prefill).
     The ticket no longer embeds the order; it names it by orderNo. */
  useEffect(() => {
    if (!ticket) return;
    const oNo = ticket.orderNo;
    if (!oNo || fetchedOrderFor.current === oNo) return;
    fetchedOrderFor.current = oNo;
    api.get(`/admin/orders/${encodeURIComponent(oNo)}`)
      .then((res) => {
        /* {order, customer, items, history, siblings} — the lines sit BESIDE
           the header, so they are folded back onto it for this screen */
        const od = res.data?.order;
        if (od) setOrder({ ...od, items: Array.isArray(res.data?.items) ? res.data.items : [] });
      })
      .catch(() => { /* order is optional context — ignore */ });
  }, [ticket]);

  /* init replacement steppers + refund amount once the order arrives */
  useEffect(() => {
    if (!order) return;
    setReplQty((prev) => {
      const items = order.items || [];
      if (prev.length === items.length) return prev;
      return items.map(() => 0);
    });
    setRefundAmount((prev) => (prev === "" ? String(rupeesOf(order.totalPaise) || "") : prev));
  }, [order]);

  const messages = useMemo(
    () => (Array.isArray(ticket?.messages) ? ticket.messages : []),
    [ticket]
  );

  useEffect(() => {
    threadEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [messages.length]);

  const reportedItems = useMemo(
    () => (Array.isArray(ticket?.items) ? ticket.items : []),
    [ticket]
  );

  /* rows for a dispatch panel: every order line, preselected with whatever
     the customer actually reported; falls back to the ticket snapshot when
     the order can't be loaded */
  const dispatchRows = useMemo(() => {
    const byItemId = new Map();
    reportedItems.forEach((r) => {
      if (r.orderItemId !== null && !byItemId.has(r.orderItemId)) byItemId.set(r.orderItemId, r);
    });

    const orderItems = Array.isArray(order?.items) ? order.items : [];
    if (orderItems.length > 0) {
      const rows = orderItems.map((oi, idx) => {
        const rep = byItemId.get(oi?.id);
        const ordered = posQty(oi?.quantity);
        return {
          orderItemId: oi?.id,
          name:     oi?.name || `Item ${oi?.lineNo ?? idx + 1}`,
          variant:  oi?.variantLabel || oi?.millLabel || "",
          ordered,
          issue:    rep ? rep.issue : "",
          reported: !!rep,
          preset:   rep ? Math.min(ordered, rep.quantity) : 0,
        };
      });
      /* a reported line the order no longer carries still deserves a row
         (byItemId is already de-duplicated, so keys stay unique) */
      byItemId.forEach((r, itemId) => {
        if (orderItems.some((oi) => oi?.id === itemId)) return;
        rows.push({
          orderItemId: itemId, name: r.name || "Item", variant: r.variant,
          ordered: r.quantity, issue: r.issue, reported: true, preset: r.quantity,
        });
      });
      return rows;
    }

    return Array.from(byItemId.values()).map((r) => ({
      orderItemId: r.orderItemId, name: r.name || "Item", variant: r.variant,
      ordered: r.quantity, issue: r.issue, reported: true, preset: r.quantity,
    }));
  }, [order, reportedItems]);

  /* seed the panel with the reported quantities, without clobbering edits
     (rows can arrive after the panel opens — the order loads async) */
  useEffect(() => {
    if (!dispatchMode) return;
    setDispatchSel((prev) => {
      let changed = false;
      const next = { ...prev };
      dispatchRows.forEach((r) => {
        if (!(r.orderItemId in next)) { next[r.orderItemId] = r.preset; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [dispatchMode, dispatchRows]);

  const orderNo = ticket?.orderNo || order?.orderNo || "";

  const resolution   = ticket?.resolution || {};
  const isResolved   = !!resolution.type;
  const status       = ticket?.status || "open";
  const isClosed     = status === "closed";
  const canResolve   = !isResolved && !isClosed;
  const canDispatch  = !isClosed;          // quick dispatch works at any status but closed
  const canMessage   = !isClosed;          // resolved tickets can still be discussed

  const customerName  = ticket?.customer?.name || "—";
  const customerPhone = ticket?.customer?.phone || "";

  const dispatchSpec  = DISPATCH[dispatchMode] || DISPATCH.replacement;
  const dispatchPicks = dispatchRows
    .map((r) => ({ orderItemId: r.orderItemId, quantity: Math.floor(Number(dispatchSel[r.orderItemId]) || 0) }))
    .filter((x) => Number.isInteger(x.orderItemId) && x.orderItemId > 0 && x.quantity > 0);

  /* ── actions ─────────────────────────────────────────────── */
  const handleSend = async () => {
    const text = reply.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post(`/admin/support/tickets/${id}/messages`, { text, byName: "Support" });
      setReply("");
      await fetchTicket(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleStatus = async (next) => {
    if (next === "closed" && !window.confirm("Close this ticket?")) return;
    setStatusBusy(true);
    try {
      await api.patch(`/admin/support/tickets/${id}/status`, { status: next });
      await fetchTicket(true);
      toast.success(next === "closed" ? "Ticket closed" : "Ticket marked in progress");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    } finally {
      setStatusBusy(false);
    }
  };

  const toggleDispatch = (mode) => {
    setDispatchMode((prev) => {
      const next = prev === mode ? "" : mode;
      if (next !== prev) setDispatchSel({});
      return next;
    });
  };

  const setPickQty = (index, qty, max) => {
    setDispatchSel((prev) => ({
      ...prev,
      [index]: Math.max(0, Math.min(max, Math.floor(Number(qty) || 0))),
    }));
  };

  const handleDispatch = async () => {
    const spec = DISPATCH[dispatchMode];
    if (!spec) return;
    if (!orderNo) {
      toast.error("This ticket has no linked order");
      return;
    }
    if (dispatchPicks.length === 0) {
      toast.error(dispatchMode === "missing"
        ? "Choose at least one missing item"
        : "Choose at least one item to replace");
      return;
    }
    setDispatchBusy(true);
    try {
      const res = await api.post(`/admin/support/tickets/${id}/${spec.path}`, {
        items: dispatchPicks,
        notes: dispatchNotes.trim(),
        byName: "Support",
      });
      const newOrderNo = newOrderNoOf(res.data);
      const text = `${spec.success}: ${dispatchPicks.length} item(s)${newOrderNo ? ` — order ${newOrderNo}` : ""}`;
      if (newOrderNo) {
        toast.success(text, {
          onClick: () => navigate(`/admin/orders/details/${newOrderNo}`),
          autoClose: 8000,
        });
      } else {
        toast.success(text);
      }
      setDispatchMode("");
      setDispatchSel({});
      setDispatchNotes("");
      await fetchTicket(true);
    } catch (err) {
      /* {code, message} with a real status — 404 is now "ticket or order
         gone", not "the route does not exist" */
      const msg = err.response?.data?.message;
      toast.error(
        msg ||
        (err.response?.status === 404
          ? "The ticket or its order could not be found."
          : "Dispatch failed")
      );
    } finally {
      setDispatchBusy(false);
    }
  };

  const handleReplacement = async () => {
    /* replQty is aligned to order.items, and a line is addressed by its
       order_item_id — an array index is not accepted any more */
    const orderItems = Array.isArray(order?.items) ? order.items : [];
    const items = replQty
      .map((q, i) => ({ orderItemId: orderItems[i]?.id, quantity: Number(q) || 0 }))
      .filter((x) => Number.isInteger(x.orderItemId) && x.orderItemId > 0 && x.quantity > 0);
    if (items.length === 0) {
      toast.error("Choose at least one item to replace");
      return;
    }
    setReplBusy(true);
    try {
      const res = await api.post(`/admin/support/tickets/${id}/replacement`, { items, notes: replNotes.trim() });
      const newOrderNo = newOrderNoOf(res.data);
      if (newOrderNo) {
        toast.success("Replacement order created — click to view it", {
          onClick: () => navigate(`/admin/orders/details/${newOrderNo}`),
          autoClose: 8000,
        });
      } else {
        toast.success("Replacement order created");
      }
      setResolveMode("");
      await fetchTicket(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create replacement");
    } finally {
      setReplBusy(false);
    }
  };

  const handleRefund = async () => {
    const amt = Number(refundAmount);
    if (!refundAmount || isNaN(amt) || amt <= 0) {
      toast.error("Enter a valid refund amount");
      return;
    }
    setRefundBusy(true);
    try {
      /* amount stays RUPEES on the wire — the service rounds it to paise */
      await api.post(`/admin/support/tickets/${id}/refund`, {
        mode: refundMode, amount: amt, notes: refundNotes.trim(),
      });
      toast.success(refundMode === "yopoints"
        ? `${inr(amt)} credited as Yo Points`
        : `Refund of ${inr(amt)} recorded`);
      setResolveMode("");
      await fetchTicket(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Refund failed");
    } finally {
      setRefundBusy(false);
    }
  };

  /* ── render ──────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-body text-center py-4">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-body text-danger text-center py-4">
            {error || "Ticket not found"}
          </div>
        </div>
      </div>
    );
  }

  const st  = STATUS_CHIP[status] || STATUS_CHIP.open;
  const cat = CATEGORY_CHIP[ticket.category] || CATEGORY_CHIP.other;
  const images = ticket.images;
  const reportedSummary = summarizeIssues(reportedItems);

  const dispatchBtn = (mode, label) => {
    const active = dispatchMode === mode;
    const primary = mode === "replacement";
    return (
      <button
        type="button"
        onClick={() => toggleDispatch(mode)}
        style={{
          padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
          border: `1.5px solid ${active ? "var(--mly-heading)" : (primary ? "var(--mly-heading)" : BRAND.honey)}`,
          background: active ? "var(--mly-heading)" : (primary ? "var(--mly-surface)" : "var(--mly-surface-2)"),
          color: active ? "var(--mly-on-accent)" : (primary ? "var(--mly-heading)" : "var(--mly-accent-2)"),
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div>
      <div className="container-fluid">

        {/* ── Header card ── */}
        <div className="card" style={{ borderRadius: 16 }}>
          <div className="card-body">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                  <button
                    type="button"
                    onClick={() => navigate("/admin/support")}
                    title="Back to tickets"
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: "1px solid var(--mly-border)",
                      background: "var(--mly-surface-2)", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                      fill="none" stroke="var(--mly-text-soft)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12" />
                      <polyline points="12 19 5 12 12 5" />
                    </svg>
                  </button>
                  <h5 style={{ margin: 0, fontWeight: 800, color: "var(--mly-heading)" }}>
                    {ticket.ticketNo || "Ticket"}
                  </h5>
                  <Chip spec={cat} />
                  <Chip spec={st} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "var(--mly-text)", marginBottom: 4 }}>
                  {ticket.subject || "—"}
                </div>
                <div style={{ fontSize: 13, color: "var(--mly-text-soft)", whiteSpace: "pre-wrap", maxWidth: 640 }}>
                  {ticket.description || ""}
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12, fontSize: 12, color: "var(--mly-text-muted)" }}>
                  <span>
                    <strong style={{ color: "var(--mly-text-soft)" }}>Customer:</strong>{" "}
                    {customerName}{customerPhone ? ` · ${customerPhone}` : ""}
                  </span>
                  {orderNo && (
                    <span>
                      <strong style={{ color: "var(--mly-text-soft)" }}>Order:</strong>{" "}
                      <Link to={`/admin/orders/details/${orderNo}`}
                        style={{ color: "var(--mly-heading)", fontWeight: 700, textDecoration: "underline" }}>
                        {orderNo}
                      </Link>
                    </span>
                  )}
                  <span>
                    <strong style={{ color: "var(--mly-text-soft)" }}>Raised:</strong> {formatDateTime(ticket.createdAt)}
                  </span>
                </div>

                {images.length > 0 && (
                  <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                    {images.map((im, i) => (
                      <img
                        key={i}
                        src={imgSrc(im)}
                        alt={`attachment ${i + 1}`}
                        onClick={() => setLightbox(imgSrc(im))}
                        style={{
                          width: 72, height: 72, objectFit: "cover",
                          borderRadius: 10, border: "1.5px solid var(--mly-border)", cursor: "zoom-in",
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* status action buttons */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {status === "open" && (
                  <button
                    type="button" disabled={statusBusy}
                    onClick={() => handleStatus("in_progress")}
                    style={{
                      padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                      border: `1.5px solid ${BRAND.honey}`, background: BRAND.cream, color: BRAND.gold,
                      cursor: statusBusy ? "not-allowed" : "pointer",
                    }}
                  >
                    Mark In Progress
                  </button>
                )}
                {!isClosed && (
                  <button
                    type="button" disabled={statusBusy}
                    onClick={() => handleStatus("closed")}
                    style={{
                      padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                      border: "1.5px solid #C9D1C4", background: "#EFF1ED", color: BRAND.stone,
                      cursor: statusBusy ? "not-allowed" : "pointer",
                    }}
                  >
                    Close Ticket
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Reported items ── */}
        {reportedItems.length > 0 && (
          <div className="card" style={{ borderRadius: 16 }}>
            <div className="card-header" style={{
              borderBottom: "1px solid var(--mly-border-soft)", display: "flex", gap: 10,
              justifyContent: "space-between", alignItems: "center", flexWrap: "wrap",
            }}>
              <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Reported items</h6>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                {reportedItems.length} item{reportedItems.length === 1 ? "" : "s"}
                {reportedSummary ? ` · ${reportedSummary}` : ""}
              </span>
            </div>
            <div className="card-body">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--mly-surface-2)" }}>
                      <th style={TH}>Item</th>
                      <th style={TH}>Variant</th>
                      <th style={{ ...TH, textAlign: "center" }}>Qty</th>
                      <th style={{ ...TH, textAlign: "center" }}>Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportedItems.map((it, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--mly-border-soft)" }}>
                        <td style={{ padding: "9px 10px", fontWeight: 600 }}>
                          {it.name || `Item ${i + 1}`}
                        </td>
                        <td style={{ padding: "9px 10px", color: "var(--mly-text-muted)" }}>
                          {it.variant || "—"}
                        </td>
                        <td style={{ padding: "9px 10px", textAlign: "center", fontWeight: 700 }}>
                          {it.quantity}
                        </td>
                        <td style={{ padding: "9px 10px", textAlign: "center" }}>
                          <Chip spec={ISSUE_CHIP[it.issue] || ISSUE_CHIP.other} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 20, alignItems: "start" }}>

          {/* ══ LEFT: message thread ══ */}
          <div className="card" style={{ borderRadius: 16, marginBottom: 20 }}>
            <div className="card-header" style={{ borderBottom: "1px solid var(--mly-border-soft)" }}>
              <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Conversation</h6>
            </div>
            <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 0 }}>

              {/* ── quick dispatch ── */}
              {canDispatch && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
                  {dispatchBtn("replacement", "Dispatch replacement")}
                  {dispatchBtn("missing", "Send missing items")}
                </div>
              )}

              {canDispatch && dispatchMode && (
                <div style={{
                  border: `1.5px solid ${BRAND.honey}`, background: "var(--mly-surface-2)",
                  borderRadius: 16, padding: 16, marginBottom: 16,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "var(--mly-heading)" }}>{dispatchSpec.title}</div>
                    <button
                      type="button"
                      onClick={() => { setDispatchMode(""); setDispatchSel({}); }}
                      title="Close panel"
                      style={{
                        border: "none", background: "transparent", color: "var(--mly-text-soft)",
                        fontSize: 18, lineHeight: 1, cursor: "pointer", padding: 2,
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--mly-text-soft)", marginBottom: 12 }}>
                    {dispatchSpec.blurb}
                  </div>

                  {!orderNo ? (
                    <div style={{ fontSize: 13, color: "var(--mly-accent)", background: "var(--mly-surface)", border: `1px solid ${BRAND.honey}`, borderRadius: 10, padding: "10px 14px" }}>
                      This ticket has no linked order, so nothing can be dispatched from it.
                    </div>
                  ) : dispatchRows.length === 0 ? (
                    <div style={{ fontSize: 13, color: "var(--mly-text-soft)", textAlign: "center", padding: "14px 0" }}>
                      {order ? "No items on this order." : "Loading order items…"}
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX: "auto", background: "var(--mly-surface)", borderRadius: 12, border: "1px solid var(--mly-border)" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: "var(--mly-surface-2)" }}>
                              <th style={{ ...TH, width: 34 }} />
                              <th style={TH}>Item</th>
                              <th style={{ ...TH, textAlign: "center" }}>Ordered</th>
                              <th style={{ ...TH, textAlign: "center" }}>Send</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dispatchRows.map((r) => {
                              const q = Math.floor(Number(dispatchSel[r.orderItemId]) || 0);
                              const max = Math.max(1, r.ordered);
                              return (
                                <tr key={r.orderItemId} style={{ borderTop: "1px solid var(--mly-border-soft)", background: r.reported ? "var(--mly-accent-soft)" : "var(--mly-surface)" }}>
                                  <td style={{ padding: "9px 10px", textAlign: "center" }}>
                                    <input
                                      type="checkbox"
                                      checked={q > 0}
                                      onChange={() => setPickQty(r.orderItemId, q > 0 ? 0 : (r.preset || 1), max)}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </td>
                                  <td style={{ padding: "9px 10px", fontWeight: 600 }}>
                                    {r.name}
                                    {r.variant && (
                                      <span style={{
                                        marginLeft: 6, background: BRAND.cream, color: BRAND.gold,
                                        border: `1px solid ${BRAND.honey}`, fontSize: 9.5, fontWeight: 700,
                                        padding: "1px 7px", borderRadius: 20, whiteSpace: "nowrap",
                                      }}>
                                        {r.variant}
                                      </span>
                                    )}
                                    {r.reported && (
                                      <span style={{ marginLeft: 6, display: "inline-block", verticalAlign: "middle" }}>
                                        <Chip spec={ISSUE_CHIP[r.issue] || ISSUE_CHIP.other} />
                                      </span>
                                    )}
                                  </td>
                                  <td style={{ padding: "9px 10px", textAlign: "center", color: "var(--mly-text-muted)" }}>
                                    {r.ordered}
                                  </td>
                                  <td style={{ padding: "9px 10px" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                      <button type="button" onClick={() => setPickQty(r.orderItemId, q - 1, max)} disabled={q <= 0}
                                        style={{
                                          width: 26, height: 26, borderRadius: 7, border: "1.5px solid var(--mly-border)",
                                          background: "var(--mly-surface)", color: q <= 0 ? "var(--mly-text-muted)" : "var(--mly-heading)",
                                          fontWeight: 800, cursor: q <= 0 ? "not-allowed" : "pointer", lineHeight: 1,
                                        }}>−</button>
                                      <span style={{ minWidth: 20, textAlign: "center", fontWeight: 700 }}>{q}</span>
                                      <button type="button" onClick={() => setPickQty(r.orderItemId, q + 1, max)} disabled={q >= max}
                                        style={{
                                          width: 26, height: 26, borderRadius: 7, border: "1.5px solid var(--mly-border)",
                                          background: "var(--mly-surface)", color: q >= max ? "var(--mly-text-muted)" : "var(--mly-heading)",
                                          fontWeight: 800, cursor: q >= max ? "not-allowed" : "pointer", lineHeight: 1,
                                        }}>+</button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <label style={LABEL}>Notes <span style={{ color: BRAND.gold, fontWeight: 400 }}>(optional)</span></label>
                        <input
                          type="text" value={dispatchNotes}
                          onChange={(e) => setDispatchNotes(e.target.value)}
                          placeholder={dispatchMode === "missing" ? "e.g. Two packs never made it into the box" : "e.g. Packet arrived torn"}
                          style={INPUT}
                          onFocus={(e) => e.target.style.borderColor = "var(--mly-accent)"}
                          onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleDispatch}
                        disabled={dispatchBusy || dispatchPicks.length === 0}
                        style={{
                          marginTop: 14, width: "100%", padding: "11px 20px", borderRadius: 10,
                          border: "none", fontWeight: 700, fontSize: 14, color: "var(--mly-on-accent)",
                          background: dispatchBusy || dispatchPicks.length === 0 ? "#a3b5ac" : "var(--mly-heading)",
                          cursor: dispatchBusy || dispatchPicks.length === 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        {dispatchBusy
                          ? "Dispatching…"
                          : `${dispatchSpec.cta}${dispatchPicks.length ? ` · ${dispatchPicks.length} item(s)` : ""}`}
                      </button>
                    </>
                  )}
                </div>
              )}

              <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, paddingRight: 4 }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: "center", color: "var(--mly-text-muted)", fontSize: 13, padding: "30px 0" }}>
                    {canMessage
                      ? "No messages yet — reply below to start the conversation."
                      : "No messages on this ticket."}
                  </div>
                ) : (
                  messages.map((m, i) => {
                    const isAdmin = m.by === "admin";
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: isAdmin ? "flex-end" : "flex-start" }}>
                        <div style={{
                          maxWidth: "78%",
                          background: isAdmin ? "var(--mly-heading)" : "var(--mly-surface)",
                          color: isAdmin ? "var(--mly-on-accent)" : "var(--mly-text)",
                          border: isAdmin ? "none" : "1.5px solid var(--mly-border)",
                          borderRadius: 14,
                          borderBottomRightRadius: isAdmin ? 4 : 14,
                          borderBottomLeftRadius: isAdmin ? 14 : 4,
                          padding: "10px 14px",
                        }}>
                          <div style={{
                            fontSize: 10.5, fontWeight: 700, marginBottom: 3,
                            color: isAdmin ? BRAND.honey : "var(--mly-text-soft)",
                            textTransform: "uppercase", letterSpacing: 0.5,
                          }}>
                            {m.author || (isAdmin ? "Support" : "Customer")}
                          </div>
                          <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{m.body}</div>
                          <div style={{ fontSize: 10, marginTop: 5, opacity: 0.65, textAlign: "right" }}>
                            {formatDateTime(m.at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={threadEndRef} />
              </div>

              {/* reply box — a resolved ticket is still a live conversation;
                  only a CLOSED ticket ends it */}
              {canMessage ? (
                <>
                  {status === "resolved" && (
                    <div style={{
                      marginTop: 14, fontSize: 11.5, color: BRAND.pine,
                      background: "#E4F0EA", border: "1px solid #9CC7B4",
                      borderRadius: 10, padding: "8px 12px",
                    }}>
                      This ticket is resolved but still open for messages — close it to end the thread.
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "flex-end" }}>
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Type a reply to the customer…"
                      rows={2}
                      style={{ ...INPUT, resize: "vertical", flex: 1 }}
                      onFocus={(e) => e.target.style.borderColor = "var(--mly-accent)"}
                      onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"}
                    />
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={sending || !reply.trim()}
                      style={{
                        padding: "11px 24px", borderRadius: 10, border: "none",
                        background: sending || !reply.trim() ? "#a3b5ac" : "var(--mly-heading)",
                        color: "var(--mly-on-accent)", fontWeight: 700, fontSize: 14,
                        cursor: sending || !reply.trim() ? "not-allowed" : "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {sending ? "Sending…" : "Send"}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{
                  marginTop: 16, textAlign: "center", fontSize: 12.5, color: BRAND.stone,
                  background: "#EFF1ED", border: "1px solid #C9D1C4",
                  borderRadius: 10, padding: "12px 14px",
                }}>
                  This ticket is closed — the conversation is read-only.
                </div>
              )}
            </div>
          </div>

          {/* ══ RIGHT: resolution ══ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* read-only resolution card */}
            {isResolved && (
              <div className="card" style={{ borderRadius: 16, border: "1.5px solid #9CC7B4", marginBottom: 0 }}>
                <div className="card-header" style={{ borderBottom: "1px solid var(--mly-border-soft)", background: "#E4F0EA", borderRadius: "16px 16px 0 0" }}>
                  <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: BRAND.pine }}>Resolution</h6>
                </div>
                <div className="card-body" style={{ fontSize: 13, color: "var(--mly-text-soft)" }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Type:</strong>{" "}
                    {resolution.type === "replacement" ? "Replacement delivery"
                      : resolution.type === "refund_source" ? "Refund to original payment source"
                      : resolution.type === "refund_points" ? "Refund as Yo Points (Reelo)"
                      : resolution.type}
                  </div>
                  {Number(resolution.amount) > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>Amount:</strong> {inr(resolution.amount)}
                    </div>
                  )}
                  {resolution.refId && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>Reference:</strong>{" "}
                      <span style={{ fontFamily: "monospace" }}>{resolution.refId}</span>
                    </div>
                  )}
                  {resolution.notes && (
                    <div><strong>Notes:</strong> {resolution.notes}</div>
                  )}
                </div>
              </div>
            )}

            {/* resolve card */}
            {canResolve && (
              <div className="card" style={{ borderRadius: 16, marginBottom: 0 }}>
                <div className="card-header" style={{ borderBottom: "1px solid var(--mly-border-soft)" }}>
                  <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Resolve this ticket</h6>
                </div>
                <div className="card-body">
                  <div style={{ display: "flex", gap: 10, marginBottom: resolveMode ? 18 : 0 }}>
                    <button
                      type="button"
                      onClick={() => setResolveMode(resolveMode === "replacement" ? "" : "replacement")}
                      style={{
                        flex: 1, padding: "10px 12px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                        border: `1.5px solid ${resolveMode === "replacement" ? "var(--mly-heading)" : "var(--mly-border)"}`,
                        background: resolveMode === "replacement" ? "var(--mly-heading)" : "var(--mly-surface)",
                        color: resolveMode === "replacement" ? "var(--mly-on-accent)" : "var(--mly-text-soft)",
                        cursor: "pointer",
                      }}
                    >
                      Replacement delivery
                    </button>
                    <button
                      type="button"
                      onClick={() => setResolveMode(resolveMode === "refund" ? "" : "refund")}
                      style={{
                        flex: 1, padding: "10px 12px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                        border: `1.5px solid ${resolveMode === "refund" ? "var(--mly-heading)" : "var(--mly-border)"}`,
                        background: resolveMode === "refund" ? "var(--mly-heading)" : "var(--mly-surface)",
                        color: resolveMode === "refund" ? "var(--mly-on-accent)" : "var(--mly-text-soft)",
                        cursor: "pointer",
                      }}
                    >
                      Refund
                    </button>
                  </div>

                  {/* ── Replacement flow (resolves the ticket) ── */}
                  {resolveMode === "replacement" && (
                    !orderNo ? (
                      <div style={{ fontSize: 13, color: "#b45309", background: BRAND.cream, border: `1px solid ${BRAND.honey}`, borderRadius: 10, padding: "10px 14px" }}>
                        This ticket has no linked order, so a replacement delivery can't be created.
                      </div>
                    ) : !order ? (
                      <div style={{ fontSize: 13, color: "var(--mly-text-muted)", textAlign: "center", padding: "14px 0" }}>
                        Loading order items…
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginBottom: 10 }}>
                          Pick the quantities to send again — a new zero-charge order is created and the ticket is marked resolved.
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: "var(--mly-surface-2)" }}>
                                <th style={TH}>Item</th>
                                <th style={{ ...TH, textAlign: "center" }}>Ordered</th>
                                <th style={{ ...TH, textAlign: "center" }}>Replace</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(order.items || []).map((item, i) => {
                                const max = Number(item.quantity) || 1;
                                const q = Number(replQty[i]) || 0;
                                const step = (d) =>
                                  setReplQty((prev) => prev.map((x, j) =>
                                    j === i ? Math.max(0, Math.min(max, (Number(x) || 0) + d)) : x));
                                return (
                                  <tr key={i} style={{ borderTop: "1px solid var(--mly-border-soft)" }}>
                                    <td style={{ padding: "9px 10px", fontWeight: 600 }}>
                                      {item.name || `Item ${i + 1}`}
                                      {(item.variantLabel || item.millLabel) && (
                                        <span style={{
                                          marginLeft: 6, background: BRAND.cream, color: BRAND.gold,
                                          border: `1px solid ${BRAND.honey}`, fontSize: 9.5, fontWeight: 700,
                                          padding: "1px 7px", borderRadius: 20, whiteSpace: "nowrap",
                                        }}>
                                          {item.variantLabel || item.millLabel}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: "9px 10px", textAlign: "center", color: "var(--mly-text-muted)" }}>
                                      {item.quantity}
                                    </td>
                                    <td style={{ padding: "9px 10px" }}>
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                        <button type="button" onClick={() => step(-1)} disabled={q <= 0}
                                          style={{
                                            width: 26, height: 26, borderRadius: 7, border: "1.5px solid var(--mly-border)",
                                            background: "var(--mly-surface)", color: q <= 0 ? "var(--mly-text-muted)" : "var(--mly-heading)",
                                            fontWeight: 800, cursor: q <= 0 ? "not-allowed" : "pointer", lineHeight: 1,
                                          }}>−</button>
                                        <span style={{ minWidth: 20, textAlign: "center", fontWeight: 700 }}>{q}</span>
                                        <button type="button" onClick={() => step(1)} disabled={q >= max}
                                          style={{
                                            width: 26, height: 26, borderRadius: 7, border: "1.5px solid var(--mly-border)",
                                            background: "var(--mly-surface)", color: q >= max ? "var(--mly-text-muted)" : "var(--mly-heading)",
                                            fontWeight: 800, cursor: q >= max ? "not-allowed" : "pointer", lineHeight: 1,
                                          }}>+</button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ marginTop: 14 }}>
                          <label style={LABEL}>Notes <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span></label>
                          <input type="text" value={replNotes} onChange={(e) => setReplNotes(e.target.value)}
                            placeholder="e.g. Packet arrived torn" style={INPUT}
                            onFocus={(e) => e.target.style.borderColor = "var(--mly-accent)"}
                            onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                        </div>
                        <button
                          type="button"
                          onClick={handleReplacement}
                          disabled={replBusy || replQty.every((q) => !(Number(q) > 0))}
                          style={{
                            marginTop: 14, width: "100%", padding: "11px 20px", borderRadius: 10,
                            border: "none", fontWeight: 700, fontSize: 14, color: "var(--mly-on-accent)",
                            background: replBusy || replQty.every((q) => !(Number(q) > 0)) ? "#a3b5ac" : "var(--mly-heading)",
                            cursor: replBusy ? "not-allowed" : "pointer",
                          }}
                        >
                          {replBusy ? "Creating…" : "Create replacement order"}
                        </button>
                      </>
                    )
                  )}

                  {/* ── Refund flow ── */}
                  {resolveMode === "refund" && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                        {[
                          { value: "source",   label: "Original payment source",
                            hint: order?.paymentMethod === "razorpay" ? "Refunded via Razorpay" : "Recorded as a manual refund" },
                          { value: "yopoints", label: "Yo Points (via Reelo)",
                            hint: "Credited to the customer's Reelo wallet" },
                        ].map((opt) => (
                          <label key={opt.value} style={{
                            display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
                            border: `1.5px solid ${refundMode === opt.value ? "var(--mly-heading)" : "var(--mly-border)"}`,
                            background: refundMode === opt.value ? "var(--mly-accent-soft)" : "var(--mly-surface)",
                            borderRadius: 10, padding: "10px 14px", marginBottom: 0,
                          }}>
                            <input
                              type="radio" name="refund-mode" value={opt.value}
                              checked={refundMode === opt.value}
                              onChange={() => setRefundMode(opt.value)}
                              style={{ marginTop: 3, cursor: "pointer" }}
                            />
                            <span>
                              <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--mly-text)" }}>{opt.label}</span>
                              <span style={{ display: "block", fontSize: 11, color: "var(--mly-text-muted)" }}>{opt.hint}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Amount (₹) <span style={{ color: "#ef4444" }}>*</span></label>
                        <input type="number" min="0" step="0.5" value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          placeholder={order ? String(rupeesOf(order.totalPaise) || "") : "e.g. 250"}
                          style={INPUT}
                          onFocus={(e) => e.target.style.borderColor = "var(--mly-accent)"}
                          onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                        {order && (
                          <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 }}>
                            Order total: {inr(rupeesOf(order.totalPaise))}
                          </div>
                        )}
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Notes <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span></label>
                        <input type="text" value={refundNotes} onChange={(e) => setRefundNotes(e.target.value)}
                          placeholder="e.g. Partial refund for damaged pack" style={INPUT}
                          onFocus={(e) => e.target.style.borderColor = "var(--mly-accent)"}
                          onBlur={(e)  => e.target.style.borderColor = "var(--mly-border)"} />
                      </div>
                      <button
                        type="button"
                        onClick={handleRefund}
                        disabled={refundBusy}
                        style={{
                          width: "100%", padding: "11px 20px", borderRadius: 10,
                          border: "none", fontWeight: 700, fontSize: 14, color: "var(--mly-on-accent)",
                          background: refundBusy ? "#a3b5ac" : "var(--mly-heading)",
                          cursor: refundBusy ? "not-allowed" : "pointer",
                        }}
                      >
                        {refundBusy ? "Processing…" : `Refund ${refundAmount ? inr(refundAmount) : ""}`}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── lightbox ── */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 2000,
            background: "rgba(0,0,0,0.75)", cursor: "zoom-out",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 30,
          }}
        >
          <img
            src={lightbox}
            alt="attachment"
            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
          />
        </div>
      )}
    </div>
  );
};

export default SupportDetail;
